import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  useState,
  type InputHTMLAttributes,
  type KeyboardEvent,
} from 'react';
import { cn } from '../../../lib/utils';
import { PlusIcon } from '../../../icons/plus';
import { XIcon } from '../../../icons/x';
import { Badge } from '../badge';

/** Tempo que a tag existente fica destacada quando o item digitado a repete. */
export const TAG_INPUT_HIGHLIGHT_MS = 1500;

/** Comparação de itens: sem caixa, sem acento, espaços colapsados. */
export function normalizeTag(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

export interface TagInputMessages {
  /** Enter/+ com o campo vazio. */
  empty?: string;
  /** Item igual a um já na lista. */
  duplicate?: string;
  /** Item acima de `maxItemLength`. */
  tooLong?: string;
}

export interface TagInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'defaultValue' | 'maxLength'
> {
  value: string[];
  onChange: (next: string[]) => void;
  /** Limite de caracteres POR ITEM (validado ao adicionar, não bloqueia a digitação). */
  maxItemLength?: number;
  messages?: TagInputMessages;
  /** Regra extra do dono do campo (ex.: item já existe em outro campo). Devolve a mensagem ou `null`. */
  validateItem?: (item: string, current: string[]) => string | null;
  /**
   * Recebe a mensagem da última tentativa de adicionar (`null` quando deu certo
   * ou o campo mudou). O dono do campo a mostra no slot de erro do FormField.
   */
  onItemError?: (message: string | null) => void;
  /** aria-label do botão `+`. */
  addButtonLabel: string;
  /** aria-label do botão de remover cada tag. */
  removeItemLabel: (item: string) => string;
  /** Raiz do testid: input → `<root>-input`, `+` → `<root>-add`, lista → `<root>-list`, remover → `<root>-remove-<índice>`. */
  'data-testid'?: string;
}

/**
 * DEV-259 AC-16..AC-18 — entrada livre com tags. Domínio-agnóstico: textos de
 * negócio chegam por props; a tag é o `Badge` outline com cruz; a lista fica
 * abaixo do campo e é ligada ao input por `aria-describedby` (a ordem visual
 * campo → dica → tags inverte a leitura; a ligação devolve o contexto).
 */
const TagInput = forwardRef<HTMLInputElement, TagInputProps>(
  (
    {
      value,
      onChange,
      maxItemLength,
      messages = {},
      validateItem,
      onItemError,
      addButtonLabel,
      removeItemLabel,
      className,
      disabled,
      'aria-describedby': ariaDescribedBy,
      'data-testid': testId,
      onKeyDown,
      ...inputProps
    },
    ref
  ) => {
    const [draft, setDraft] = useState('');
    const [highlighted, setHighlighted] = useState<number | null>(null);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const listId = useId();

    useEffect(
      () => () => {
        if (timer.current) clearTimeout(timer.current);
      },
      []
    );

    const highlight = (index: number) => {
      setHighlighted(index);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setHighlighted(null), TAG_INPUT_HIGHLIGHT_MS);
    };

    const report = (message: string | null) => onItemError?.(message);

    const add = () => {
      const item = draft.trim().replace(/\s+/g, ' ');
      if (!item) {
        report(messages.empty ?? null);
        return;
      }
      if (maxItemLength !== undefined && item.length > maxItemLength) {
        report(messages.tooLong ?? null);
        return;
      }
      const key = normalizeTag(item);
      const existing = value.findIndex((v) => normalizeTag(v) === key);
      if (existing >= 0) {
        highlight(existing);
        report(messages.duplicate ?? null);
        return;
      }
      const external = validateItem?.(item, value) ?? null;
      if (external) {
        report(external);
        return;
      }
      onChange([...value, item]);
      setDraft('');
      report(null);
    };

    const remove = (index: number) => {
      onChange(value.filter((_, i) => i !== index));
      setHighlighted(null);
      report(null);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(event);
      if (event.defaultPrevented) return;
      if (event.key === 'Enter') {
        event.preventDefault();
        add();
      }
    };

    const describedBy = [ariaDescribedBy, value.length > 0 ? listId : undefined]
      .filter(Boolean)
      .join(' ');

    return (
      <div data-slot="tag-input" className={cn('flex flex-col gap-2', className)}>
        <div className="relative">
          <input
            ref={ref}
            data-slot="tag-input-input"
            data-testid={testId ? `${testId}-input` : undefined}
            type="text"
            value={draft}
            disabled={disabled}
            aria-describedby={describedBy || undefined}
            onChange={(event) => {
              setDraft(event.target.value);
              report(null);
            }}
            onKeyDown={handleKeyDown}
            className={cn(
              'flex h-11 w-full rounded-md border border-input bg-background py-2 pl-3 pr-12 text-sm text-foreground shadow-2xs',
              'outline-none transition-colors placeholder:text-muted-foreground',
              'focus-visible:ring-2 focus-visible:ring-primary/40',
              'disabled:cursor-not-allowed disabled:opacity-50',
              // Campo com erro (Figma 20080:1334, mesmo padrão do Input).
              'aria-[invalid=true]:border-destructive aria-[invalid=true]:shadow-none aria-[invalid=true]:ring-[3px] aria-[invalid=true]:ring-destructive-60'
            )}
            {...inputProps}
          />
          <button
            type="button"
            aria-label={addButtonLabel}
            data-testid={testId ? `${testId}-add` : undefined}
            disabled={disabled}
            onClick={add}
            className={cn(
              'absolute right-0 top-0 inline-flex size-11 items-center justify-center rounded-r-md text-muted-foreground',
              'hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
              'disabled:cursor-not-allowed disabled:opacity-50'
            )}
          >
            <PlusIcon className="size-4" aria-hidden />
          </button>
        </div>
        {value.length > 0 ? (
          <ul
            id={listId}
            role="list"
            data-slot="tag-input-list"
            data-testid={testId ? `${testId}-list` : undefined}
            className="flex flex-wrap gap-2"
          >
            {value.map((item, index) => (
              <li
                key={`${item}-${index}`}
                data-highlighted={highlighted === index ? 'true' : 'false'}
                className="transition-transform data-[highlighted=true]:scale-105"
              >
                <Badge
                  variant="outline"
                  className={cn(
                    'min-h-7 gap-1 pr-0.5',
                    highlighted === index && 'border-primary ring-2 ring-primary/40'
                  )}
                  rightIcon={
                    <button
                      type="button"
                      aria-label={removeItemLabel(item)}
                      data-testid={testId ? `${testId}-remove-${index}` : undefined}
                      disabled={disabled}
                      onClick={() => remove(index)}
                      className={cn(
                        'inline-flex size-6 items-center justify-center rounded text-muted-foreground',
                        'hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                        'disabled:cursor-not-allowed'
                      )}
                    >
                      <XIcon aria-hidden />
                    </button>
                  }
                >
                  {item}
                </Badge>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  }
);
TagInput.displayName = 'TagInput';

export { TagInput };
